import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageHeader } from '@/components/layout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Plus,
  Search,
  RefreshCw,
} from 'lucide-react'
import { PresupuestosTable } from '@/components/presupuestos/PresupuestosTable'
import { EnviarPresupuestoDialog } from '@/components/presupuestos/EnviarPresupuestoDialog'
import {
  usePresupuestos,
  useEliminarPresupuesto,
  useCambiarEstadoPresupuesto,
  useDuplicarPresupuesto,
} from '@/hooks/usePresupuestos'
import type { Presupuesto, EstadoPresupuesto } from '@/types/database'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { usePermissions } from '@/hooks/usePermissions'

export function Presupuestos() {
  const navigate = useNavigate()
  const { puede } = usePermissions()

  // Estado para filtros
  const [busqueda, setBusqueda] = useState('')
  const [filtroEstado, setFiltroEstado] = useState<string>('todos')

  // Estado para dialogs (solo los necesarios)
  const [presupuestoEliminar, setPresupuestoEliminar] = useState<Presupuesto | null>(null)
  const [presupuestoEnviar, setPresupuestoEnviar] = useState<Presupuesto | null>(null)

  // Queries y mutations
  const {
    data: presupuestos = [],
    isLoading,
    error,
    refetch,
  } = usePresupuestos({
    estado: filtroEstado !== 'todos' ? (filtroEstado as EstadoPresupuesto) : undefined,
  })

  const eliminarPresupuesto = useEliminarPresupuesto()
  const cambiarEstado = useCambiarEstadoPresupuesto()
  const duplicarPresupuesto = useDuplicarPresupuesto()

  // Filtrar por busqueda local
  const presupuestosFiltrados = presupuestos.filter((p) => {
    if (!busqueda) return true
    const termino = busqueda.toLowerCase()
    return (
      (p.cliente_nombre || '').toLowerCase().includes(termino) ||
      (p.numero_presupuesto || 0).toString().includes(termino) ||
      p.cliente_cuit?.toLowerCase().includes(termino)
    )
  })

  // Handlers
  const handleNuevoPresupuesto = () => {
    navigate('/presupuestos/nuevo')
  }

  const handleEditarPresupuesto = (presupuesto: Presupuesto) => {
    navigate(`/presupuestos/${presupuesto.id}/editar`)
  }

  const handleEliminarPresupuesto = (presupuesto: Presupuesto) => {
    setPresupuestoEliminar(presupuesto)
  }

  const handleDuplicarPresupuesto = async (presupuesto: Presupuesto) => {
    await duplicarPresupuesto.mutateAsync(presupuesto.id)
  }

  const handleCambiarEstado = async (presupuesto: Presupuesto, estado: EstadoPresupuesto) => {
    // "Enviado" opens dialog to send by email instead of changing state directly
    if (estado === 'enviado') {
      setPresupuestoEnviar(presupuesto)
      return
    }

    await cambiarEstado.mutateAsync({ id: presupuesto.id, estado })
  }

  const handleVerDetalle = (presupuesto: Presupuesto) => {
    navigate(`/presupuestos/${presupuesto.id}`)
  }

  const handleConfirmarEliminar = async () => {
    if (!presupuestoEliminar) return
    await eliminarPresupuesto.mutateAsync(presupuestoEliminar.id)
    setPresupuestoEliminar(null)
  }

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Presupuestos"
        description="Generador de presupuestos"
      />

      <main className="flex-1 min-h-0 p-6 flex flex-col gap-6">

        {/* Barra de acciones */}
        <div className="shrink-0 flex flex-col sm:flex-row gap-4 justify-between">
          <div className="flex flex-1 gap-4 flex-wrap">
            {/* Busqueda */}
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por cliente o numero..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Filtro por estado */}
            <Select value={filtroEstado} onValueChange={setFiltroEstado}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="borrador">Borrador</SelectItem>
                <SelectItem value="enviado">Enviado</SelectItem>
                <SelectItem value="aceptado">Aceptado</SelectItem>
                <SelectItem value="rechazado">Rechazado</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" size="icon" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4" />
            </Button>
            {puede.crearPresupuesto && (
              <Button onClick={handleNuevoPresupuesto}>
                <Plus className="h-4 w-4 mr-2" />
                Nuevo Presupuesto
              </Button>
            )}
          </div>
        </div>

        {/* Tabla de presupuestos */}
        {error ? (
          <div className="shrink-0 rounded-lg border border-destructive/50 bg-destructive/10 p-4">
            <p className="text-destructive">
              Error al cargar los presupuestos. Verifica la conexion con Supabase.
            </p>
          </div>
        ) : (
          <div className="flex-1 min-h-0">
            <PresupuestosTable
              presupuestos={presupuestosFiltrados}
              isLoading={isLoading}
              onEditar={handleEditarPresupuesto}
              onEliminar={handleEliminarPresupuesto}
              onDuplicar={handleDuplicarPresupuesto}
              onCambiarEstado={handleCambiarEstado}
              onVerDetalle={handleVerDetalle}
              onRowClick={handleVerDetalle}
              canEdit={puede.editarPresupuesto}
              canDelete={puede.eliminarPresupuesto}
              canCreate={puede.crearPresupuesto}
              canEnviar={puede.enviarPresupuesto}
              canAceptar={puede.aceptarPresupuesto}
              canRechazar={puede.rechazarPresupuesto}
            />
          </div>
        )}
      </main>

      {/* Dialog de confirmacion para eliminar */}
      <AlertDialog
        open={!!presupuestoEliminar}
        onOpenChange={(open) => !open && setPresupuestoEliminar(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar presupuesto?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta accion no se puede deshacer. Se eliminara permanentemente el
              presupuesto <strong>#{presupuestoEliminar?.numero_presupuesto}</strong> para{' '}
              <strong>{presupuestoEliminar?.cliente_nombre}</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmarEliminar}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {eliminarPresupuesto.isPending ? 'Eliminando...' : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog para enviar presupuesto por email */}
      <EnviarPresupuestoDialog
        open={!!presupuestoEnviar}
        onOpenChange={(open) => !open && setPresupuestoEnviar(null)}
        presupuesto={presupuestoEnviar}
      />
    </div>
  )
}
